/**
 * メッセージスレッド API サービス
 * 新しいスレッドベースのメッセージシステム用
 */

import axios from 'axios';
import {
  ThreadListQuery,
  ThreadListResponse,
  ThreadDetailResponse,
  SendMessageRequest,
  SendMessageResponse,
  UpdateThreadStatusRequest,
  UpdateThreadStatusResponse,
  MarkAsReadResponse,
} from '../types/message-thread';

const apiClient = axios.create({
  withCredentials: true, // Cookie認証を使用
  headers: {
    'Content-Type': 'application/json',
  },
});

// レスポンスインターセプター（エラーハンドリング）
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export const messageThreadService = {
  // 企業側API

  /**
   * 企業のメッセージスレッド一覧取得
   */
  async getCompanyThreads(
    companyId: number,
    query: ThreadListQuery = {}
  ): Promise<ThreadListResponse> {
    const params = new URLSearchParams();

    if (query.page) params.append('page', query.page.toString());
    if (query.limit) params.append('limit', query.limit.toString());
    if (query.sortBy) params.append('sortBy', query.sortBy);
    if (query.sortOrder) params.append('sortOrder', query.sortOrder);
    if (query.status) params.append('status', query.status);
    if (query.search) params.append('search', query.search);
    if (query.includeArchived !== undefined)
      params.append('includeArchived', String(query.includeArchived));
    if (query.includeBlocked !== undefined)
      params.append('includeBlocked', String(query.includeBlocked));
    if (query.onlyArchived !== undefined)
      params.append('onlyArchived', String(query.onlyArchived));
    if (query.onlyBlocked !== undefined)
      params.append('onlyBlocked', String(query.onlyBlocked));

    const response = await apiClient.get(
      `/api/company/${companyId}/messages/threads?${params.toString()}`
    );
    return response.data;
  },

  /**
   * 企業側スレッド詳細取得
   */
  async getCompanyThreadDetail(
    companyId: number,
    threadId: string
  ): Promise<ThreadDetailResponse> {
    const response = await apiClient.get(
      `/api/company/${companyId}/messages/threads/${threadId}`
    );
    return response.data;
  },

  /**
   * 企業からの返信送信
   */
  async sendCompanyReply(
    companyId: number,
    threadId: string,
    messageData: SendMessageRequest
  ): Promise<SendMessageResponse> {
    const response = await apiClient.post(
      `/api/company/${companyId}/messages/threads/${threadId}/reply`,
      messageData
    );
    return response.data;
  },

  /**
   * 企業側スレッド状態更新
   */
  async updateCompanyThreadStatus(
    companyId: number,
    threadId: string,
    statusData: UpdateThreadStatusRequest
  ): Promise<UpdateThreadStatusResponse> {
    const response = await apiClient.put(
      `/api/company/${companyId}/messages/threads/${threadId}/status`,
      statusData
    );
    return response.data;
  },

  /**
   * 企業側メッセージ既読更新
   */
  async markCompanyMessagesAsRead(
    companyId: number,
    threadId: string
  ): Promise<MarkAsReadResponse> {
    const response = await apiClient.put(
      `/api/company/${companyId}/messages/threads/${threadId}/read`
    );
    return response.data;
  },

  // 学生側API

  /**
   * 学生のメッセージスレッド一覧取得
   */
  async getStudentThreads(
    query: ThreadListQuery = {}
  ): Promise<ThreadListResponse> {
    const params = new URLSearchParams();

    if (query.page) params.append('page', query.page.toString());
    if (query.limit) params.append('limit', query.limit.toString());
    if (query.sortBy) params.append('sortBy', query.sortBy);
    if (query.sortOrder) params.append('sortOrder', query.sortOrder);
    if (query.status) params.append('status', query.status);
    if (query.search) params.append('search', query.search);
    if (query.includeArchived !== undefined)
      params.append('includeArchived', String(query.includeArchived));
    if (query.includeBlocked !== undefined)
      params.append('includeBlocked', String(query.includeBlocked));
    if (query.onlyArchived !== undefined)
      params.append('onlyArchived', String(query.onlyArchived));
    if (query.onlyBlocked !== undefined)
      params.append('onlyBlocked', String(query.onlyBlocked));

    const response = await apiClient.get(
      `/api/messages/threads?${params.toString()}`
    );
    return response.data;
  },

  /**
   * 学生側スレッド詳細取得
   */
  async getStudentThreadDetail(
    threadId: string
  ): Promise<ThreadDetailResponse> {
    const response = await apiClient.get(`/api/messages/threads/${threadId}`);
    return response.data;
  },

  /**
   * 学生からの返信送信
   */
  async sendStudentReply(
    threadId: string,
    messageData: SendMessageRequest
  ): Promise<SendMessageResponse> {
    const response = await apiClient.post(
      `/api/messages/threads/${threadId}/reply`,
      messageData
    );
    return response.data;
  },

  /**
   * 学生側スレッド状態更新
   */
  async updateStudentThreadStatus(
    threadId: string,
    statusData: UpdateThreadStatusRequest
  ): Promise<UpdateThreadStatusResponse> {
    const response = await apiClient.put(
      `/api/messages/threads/${threadId}/status`,
      statusData
    );
    return response.data;
  },

  /**
   * 学生側メッセージ既読更新
   */
  async markStudentMessagesAsRead(
    threadId: string
  ): Promise<MarkAsReadResponse> {
    const response = await apiClient.put(
      `/api/messages/threads/${threadId}/read`
    );
    return response.data;
  },
};

export default messageThreadService;
